import { handleBisFormSubmit } from './bisFormHandler.js';

class ProductFormHandler {
  constructor(container = null) {
    try {
      // If container is provided and is a string, try to find the element
      if (typeof container === 'string') {
        container = document.querySelector(container);
      }

      // Fall back to document if no container is provided
      if (!container) {
        container = document;
      }

      // Set container
      this.container = container;

      // Choose the appropriate approach to find the form element
      if (container === document) {
        // For document, use direct selector
        this.formElement = document.querySelector('.product-form');
      } else if (container.classList.contains('product-form')) {
        // If container is the form itself
        this.formElement = container;
      } else {
        // Look within the container
        this.formElement = container.querySelector('.product-form');
      }

      // Check if we found a form element
      if (!this.formElement) {
        return;
      }

      // Initialize the handler
      this.productId = this.formElement.querySelector('form')?.dataset.productid;
      this.initElements();
      this.initVariantStock();
      this.initMostCommonSubscription();
      this.bindEvents();
      this.initUI();
      this.displayFmfTrigger();
    } catch (error) {
      // Silent error handling for production
    }
  }

  initElements() {
    try {
      this.elements = {
        form: this.formElement.querySelector('form'),

        // Back in stock notification
        bisForm: this.container.querySelector('form.notify-form'),
        messageContainer: this.container === document ?
                         document.getElementById('bis-message-container') :
                         this.container.querySelector('#bis-message-container'),

        // Submit buttons
        mainSubmit: this.formElement.querySelector('input[type=submit]'),
        mobileFixedSubmit: this.container.querySelector('.mobile-fixed-submit'),

        // Quantity controls
        quantityWrapper: this.formElement.querySelector('.quantity-wrapper'),
        quantityInput: this.formElement.querySelector('.quantity-input'),
        quantityMinus: this.formElement.querySelector('.quantity-select .minus'),
        quantityValue: this.formElement.querySelector('.quantity-select .quantity'),
        quantityPlus: this.formElement.querySelector('.quantity-select .plus'),

        // Total price
        subtotalElement: this.container === document ?
                        document.getElementById('subtotal') :
                        this.container.querySelector('#subtotal'),

        // Out of stock notification
        oosNotification: this.formElement.querySelector('.oos-notification'),

        // Read more link
        readMore: this.formElement.querySelector('.read-more'),

        // Variant selection
        productIdSelect: this.formElement.querySelector('#product-id'),
        variantRadios: this.formElement.querySelectorAll('.variant-size-radio'),

        // Purchase type selection
        purchaseTypeRadios: this.formElement.querySelectorAll('.purchase-type-radio'),
        sellingPlanSelect: this.formElement.querySelector('#selling_plan_select'),
        subscriptionPriceContainers: this.formElement.querySelectorAll('#subscription-price-display .variant-price-container'),
        oneTimePriceContainers: this.formElement.querySelectorAll('#one-time-price-display .variant-price-container'),

        // Legacy selectors (maintained for compatibility)
        willLast: this.formElement.querySelectorAll('.will-last'),
      };
    } catch (err) {
      // Silent error handling for production
    }
  }

  initVariantStock() {
    // This would be populated from Liquid in the actual implementation
    // For now, we'll leave it as an empty object to be filled by the template
  }

  bindEvents() {
    // Back in stock notification form
    if (this.elements.bisForm) {
      this.elements.bisForm.addEventListener('submit', (event) => handleBisFormSubmit(event, this.elements.bisForm));
    }

    // Mobile fixed submit button
    if (this.elements.mobileFixedSubmit) {
      window.addEventListener('scroll', this.handleFixedSubmitScroll.bind(this));
      this.elements.mobileFixedSubmit.addEventListener('click', this.handleMobileFixedSubmitClick.bind(this));
    }

    // Variant selection radios
    if (this.elements.variantRadios.length > 0) {
      this.elements.variantRadios.forEach(radio => {
        radio.addEventListener('change', this.handleVariantRadioChange.bind(this));
      });
    }

    // Disabled as the prices have been hidden on the variant size options
    // Purchase type radios (subscription/one-time)
    // if (this.elements.purchaseTypeRadios.length > 0) {
    //   this.elements.purchaseTypeRadios.forEach(radio => {
    //     radio.addEventListener('change', this.handlePurchaseTypeChange.bind(this));
    //   });
    // }

    // Select subscription purchase type on frequency change
    if (this.elements.sellingPlanSelect) {
      this.elements.sellingPlanSelect.addEventListener('change', this.selectSubscriptionPurchaseType.bind(this));
    }

    // Quantity controls
    if (this.elements.quantityInput) {
      this.elements.quantityMinus.addEventListener('click', this.handleQuantityDecrement.bind(this));
      this.elements.quantityPlus.addEventListener('click', this.handleQuantityIncrement.bind(this));
    }

    // Read more link
    if (this.elements.readMore && window.templateName === 'product') {
      this.elements.readMore.addEventListener('click', this.handleReadMoreClick.bind(this));
    }
  }

  initUI() {
    // Check for variant parameter in URL and select the corresponding radio
    const urlParams = new URLSearchParams(window.location.search);
    const variantFromUrl = urlParams.get('variant');

    if (variantFromUrl && this.elements.variantRadios.length > 0) {
      const targetRadio = Array.from(this.elements.variantRadios).find(radio => radio.value === variantFromUrl);
      if (targetRadio) {
        targetRadio.checked = true;
        this.handleVariantRadioChange({ target: targetRadio });
      }
    } else if (this.elements.variantRadios.length > 0) {
      // init the page with the first variant selected if available
      const firstCheckedVariant = this.elements.variantRadios[0];
      if (firstCheckedVariant.checked) {
        const variantSentAs = firstCheckedVariant.dataset.variantSentAs;

        this.updateProductIdSelect(firstCheckedVariant.value);
        this.updatePriceDisplays(firstCheckedVariant.value);
        this.updateBestValuePill(firstCheckedVariant.value);
        if(variantSentAs){
          this.updateSentAsText(variantSentAs);
        }
      }
    }

    // init find my food data for duration calculation
    this.initFindMyFood();

    // init swiper components if needed
    this.initSwipers();
  }

  handleFixedSubmitScroll() {
    const fixedButton = document.querySelector(`.product-${this.productId} .mobile-fixed-submit`);
    const submitButton = document.querySelector(`.product-${this.productId} .product-submit-wrapper input[type="submit"]`);

    if (fixedButton && submitButton) {
      const submitButtonOffsetTop = submitButton.offsetTop;
      const scrollY = window.scrollY || window.pageYOffset;

      if (submitButtonOffsetTop && scrollY >= submitButtonOffsetTop) {
        fixedButton.classList.add('active');
      } else {
        fixedButton.classList.remove('active');
      }
    }
  }

  handleMobileFixedSubmitClick(e) {
    e.preventDefault();
    const productForm = document.querySelector(`.product-${this.productId} .product-form`);
    if (productForm) {
      const productFormPosition = productForm.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: productFormPosition - 70, behavior: 'smooth' });
    }
  }

  handleVariantRadioChange(e) {
    if (e.target.checked) {
      const variantId = e.target.value;
      const variantSentAs = e.target.dataset.variantSentAs;

      this.updateProductIdSelect(variantId);
      this.updatePriceDisplays(variantId);
      this.updateBestValuePill(variantId);
      this.updateSentAsText(variantSentAs);

      // Update the subtotal with the new variant price
      this.updateSubtotalForSelectedVariant();

      // Update URL with variant parameter
      this.updateUrlWithVariant(variantId);
    }
  }

  updateProductIdSelect(variantId) {
    if (this.elements.productIdSelect) {
      const options = this.elements.productIdSelect.querySelectorAll('option');
      options.forEach(option => {
        option.selected = option.value === variantId;
      });

      // Trigger change event to notify Shopify's cart system
      const event = new Event('change', { bubbles: true });
      this.elements.productIdSelect.dispatchEvent(event);
    }
  }

  updatePriceDisplays(variantId) {
    // Update subscription price display
    this.elements.subscriptionPriceContainers.forEach(container => {
      container.style.display = container.getAttribute('data-variant-id') === variantId ? 'flex' : 'none';
    });

    // Update one-time price display
    this.elements.oneTimePriceContainers.forEach(container => {
      container.style.display = container.getAttribute('data-variant-id') === variantId ? 'flex' : 'none';
    });
  }

  updateBestValuePill(variantId) {
    // Update the best value pill display
    const bestValuePills = document.querySelectorAll('.best-value-pill.one-time');
    const subscriptionPill = document.querySelector('.best-value-pill.subscription');
    const normalValueText = subscriptionPill?.getAttribute('data-normal-text');
    const altValueText = subscriptionPill?.getAttribute('data-alt-text');

    bestValuePills.forEach(pill => {
      if (pill.getAttribute('data-variant-id') === variantId) {
        pill.classList.add('selected');
      } else {
        pill.classList.remove('selected');
      }
    });

    // Check if any best value pill matches the selected variant
    const hasMatchingBestValuePill = Array.from(bestValuePills).some(pill => pill.getAttribute('data-variant-id') === variantId);

    // Update the subscription pill text based on the selected variant
    if (!subscriptionPill) {
      return;
    }

    if (hasMatchingBestValuePill) {
      if (normalValueText) {
        subscriptionPill.innerHTML = normalValueText;
      }
    } else if (altValueText) {
      subscriptionPill.innerHTML = altValueText;
    }
  }

  updateSentAsText(variantSentAsText) {
    const variantSentAsElem = document.querySelector('.variant-sent-as');
    if (!variantSentAsElem) {
      return;
    }
    variantSentAsElem.innerHTML = '';
    if(variantSentAsText != undefined || variantSentAsText != null){
      variantSentAsElem.innerHTML = variantSentAsText;
    }
  }

  updateUrlWithVariant(variantId) {
    const url = new URL(window.location);
    url.searchParams.set('variant', variantId);

    // Update the URL without triggering a page reload
    window.history.replaceState({}, '', url);
  }

  // Disabled as the prices have been hidden on the variant size options
  // handlePurchaseTypeChange(e) {
  //   if (e.target.checked) {
  //     // Update the subtotal with the new price type
  //     this.updateSubtotalForSelectedVariant();

  //     // Update the variant price for the selected purchase type
  //     this.updateVariantPricesForSelectedPurchaseType();
  //   }
  // }

  updateSubtotalForSelectedVariant() {
    // Get currently selected variant
    const selectedVariant = document.querySelector('.variant-size-radio:checked');
    if (!selectedVariant) return;

    const variantId = selectedVariant.value;

    // Get currently selected purchase type
    const selectedPurchaseType = document.querySelector('.purchase-type-radio:checked');
    if (!selectedPurchaseType) return;

    const purchaseType = selectedPurchaseType.value;

    // Get the visible price container
    let visiblePriceContainer;
    if (purchaseType === 'subscription') {
      visiblePriceContainer = document.querySelector(`#subscription-price-display .variant-price-container[data-variant-id="${variantId}"]`);
    } else {
      visiblePriceContainer = document.querySelector(`#one-time-price-display .variant-price-container[data-variant-id="${variantId}"]`);
    }

    if (visiblePriceContainer && this.elements.subtotalElement) {
      const nowPrice = visiblePriceContainer.querySelector('.now-price').textContent;
      this.elements.subtotalElement.textContent = nowPrice;
    }
  }

  updateVariantPricesForSelectedPurchaseType() {
    // Get currently selected purchase type
    const selectedPurchaseType = document.querySelector('.purchase-type-radio:checked');
    if (!selectedPurchaseType) return;

    const purchaseType = selectedPurchaseType.value;

    // Show the correct price based on the selected purchase type
    const sellingPlanPrice = document.querySelectorAll('.variant-price .selling-plan-price');
    const oneTimePrice = document.querySelectorAll('.variant-price .one-time-price');

    const priceElements = purchaseType === 'subscription' ? sellingPlanPrice : oneTimePrice;
    if (priceElements) {
      priceElements.forEach(element => {
        element.style.display = 'block';
      });
    }

    const hiddenPriceElements = purchaseType !== 'subscription' ? sellingPlanPrice : oneTimePrice;
    if (hiddenPriceElements) {
      hiddenPriceElements.forEach(element => {
        element.style.display = 'none';
      });
    }
  }

  selectSubscriptionPurchaseType() {
    const subscriptionRadio = document.querySelector('.purchase-type-radio#purchase-subscription');
    if (subscriptionRadio) {
      subscriptionRadio.checked = true;
    }

    // Update the variant prices for the selected purchase type
    this.updateVariantPricesForSelectedPurchaseType();
  }

  handleQuantityDecrement() {
    const currentValue = parseInt(this.elements.quantityInput.value, 10);
    if (currentValue > 1) {
      this.updateQuantity(currentValue - 1);
    }
  }

  handleQuantityIncrement() {
    const currentValue = parseInt(this.elements.quantityInput.value, 10);
    const max = parseInt(this.elements.quantityInput.getAttribute("max") || 99999, 10);

    if (currentValue < max) {
      this.updateQuantity(currentValue + 1);
    }
  }

  updateQuantity(newValue) {
    // Set quantity in input
    this.elements.quantityInput.value = newValue;

    // Update displayed quantity text
    if (this.elements.quantityValue) {
      this.elements.quantityValue.innerHTML = newValue;
    }

    // Calculate and update total price based on quantity
    this.updateSubtotalWithQuantity(newValue);
  }

  updateSubtotalWithQuantity(quantity) {
    // Get currently selected variant and purchase type
    const selectedVariant = document.querySelector('.variant-size-radio:checked');
    const selectedPurchaseType = document.querySelector('.purchase-type-radio:checked');

    if (!selectedVariant || !selectedPurchaseType || !this.elements.subtotalElement) return;

    const variantId = selectedVariant.value;
    const purchaseType = selectedPurchaseType.value;

    // Get the visible price container
    let priceContainer;
    if (purchaseType === 'subscription') {
      priceContainer = document.querySelector(`#subscription-price-display .variant-price-container[data-variant-id="${variantId}"]`);
    } else {
      priceContainer = document.querySelector(`#one-time-price-display .variant-price-container[data-variant-id="${variantId}"]`);
    }

    if (priceContainer) {
      const priceText = priceContainer.querySelector('.now-price').textContent;
      // Extract numeric value from price (removing currency symbol and formatting)
      const priceValue = parseFloat(priceText.replace(/[^0-9.-]+/g, ''));

      if (!isNaN(priceValue)) {
        // Format the total price
        const totalPrice = priceValue * quantity;

        // Use the same currency format as the original price
        const currencySymbol = priceText.trim().charAt(0);
        this.elements.subtotalElement.textContent = `${currencySymbol}${totalPrice.toFixed(2)}`;
      }
    }
  }

  handleReadMoreClick(e) {
    e.preventDefault();
    const productDescription = document.querySelector('.product-information-accordions');
    if (productDescription) {
      const position = productDescription.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: position - 70, behavior: 'smooth' });
    }
  }

  initMostCommonSubscription() {
    if(this.elements.sellingPlanSelect){
      const mostCommon = this.elements.sellingPlanSelect.getAttribute('data-most-common');

      if (mostCommon) {
        const sellingPlanOptions = this.elements.sellingPlanSelect.querySelectorAll('option');
        sellingPlanOptions.forEach(option => {
          if (option.innerHTML.toLowerCase().includes(mostCommon)) {
            option.setAttribute('selected', 'selected');
            option.innerHTML = option.innerHTML + " (Most popular)";
          }

          // Build a box frequency
          const buildABoxFrequency = document.querySelector('#bab-frequency');
          if (buildABoxFrequency) {
            const buildABoxFrequencyValue = buildABoxFrequency.value.trim();
            const optionValue = option.textContent.trim();

            if (optionValue === buildABoxFrequencyValue) {
              setTimeout(() => {
                option.selected = true;
              }, 250);
            }
          }
        });
      }
    }
  }

  initFindMyFood() {
    const storageData = localStorage.getItem('FMF');
    if (!storageData || !this.elements.willLast.length) return;

    try {
      const fmfData = JSON.parse(storageData);
      const ageGroup = this.elements.form.dataset.lifestageFilter || '';
      let ageMatch = false;

      if (ageGroup === 'Adult' && fmfData.find_my_food_age === '1-7 years adult') {
        ageMatch = true;
      } else if (ageGroup === 'Puppy' && fmfData.find_my_food_age === '8 - 52 weeks puppy') {
        ageMatch = true;
      } else if (ageGroup === 'Senior' && fmfData.find_my_food_age === '7+ senior') {
        ageMatch = true;
      }

      if (ageMatch) {
        this.elements.willLast.forEach(element => {
          if (element.getAttribute('data-type') === "tray") {
            if (fmfData.find_my_food_wet_grams !== 0) {
              const productSize = parseFloat(element.getAttribute('data-size'));
              const traySize = parseFloat(element.getAttribute('data-tray-size'));
              const days = Math.round(productSize / (fmfData.find_my_food_wet_grams / traySize));
              element.innerHTML = `Last up to ${days} days`;
              element.classList.add('active');
            }
          } else {
            if (fmfData.find_my_food_dry_grams !== 0) {
              const productSize = parseFloat(element.getAttribute('data-size'));
              const days = Math.round((productSize * 1000) / fmfData.find_my_food_dry_grams);
              element.innerHTML = `Last up to ${days} days`;
              element.classList.add('active');
            }
          }
        });
      }
    } catch (e) {
      console.error('Error parsing FMF data:', e);
    }
  }

  handleDisabledSubscriptions() {
    // This would be controlled by a Liquid variable in the actual implementation
    // const disableSubscriptions = {{ settings.disable_subscriptions }};
    const disableSubscriptions = false;

    if (disableSubscriptions) {
      const otpHeader = document.querySelector('.sub_tab_header.ot-header');
      if (otpHeader) {
        otpHeader.click();
      }
    }
  }

  initSwipers() {
    // init CTL swiper if available
    const ctlSwiper = document.querySelector('.swiper.ctl-swiper');
    if (ctlSwiper && window.swiper) {
      new window.swiper('.swiper.ctl-swiper', {
        slidesPerView: 1.25,
        spaceBetween: 20,
        navigation: {
          nextEl: '.ctl-swiper-button-next',
          prevEl: '.ctl-swiper-button-prev',
        },
        breakpoints: {
          992: {
            slidesPerView: 1.5,
            spaceBetween: 20,
          }
        },
      });
    }

    // init alternatives products swiper if available
    const alternativesSwiper = document.querySelector('.alternatives-products-slider');
    if (alternativesSwiper && window.swiper) {
      new window.swiper(".alternatives-products-slider", {
        slidesPerView: 1.7,
        spaceBetween: 20,
        centeredSlides: true,
        lazy: {
          enabled: true,
          checkInView: true,
          loadOnTransitionStart: true,
          loadPrevNext: true,
          loadPrevNextAmount: 1
        },
        breakpoints: {
          768: {
            slidesPerView: 3.5,
            centeredSlides: true,
          },
          1200: {
            slidesPerView: 4,
            spaceBetween: 30,
            centeredSlides: false,
          }
        },
        navigation: {
          nextEl: '.alternatives-products-swiper-button-next.swiper-button-next',
          prevEl: '.alternatives-products-swiper-button-prev.swiper-button-prev',
        },
      });
    }
  }

  displayFmfTrigger() {
    const fmfTrigger = document.querySelector('.open-fmf');
    if (fmfTrigger) {
      fmfTrigger.style.display = 'block';
      fmfTrigger.style.visibility = 'hidden';
      fmfTrigger.style.opacity = 0;

      window.addEventListener('scroll', () => {
        const mainProductRect = document.querySelector('.main-product').getBoundingClientRect();
        if (mainProductRect.y + mainProductRect.height < 0) {
          fmfTrigger.style.visibility = 'visible';
          fmfTrigger.style.opacity = 100;
        } else {
          fmfTrigger.style.visibility = 'hidden';
          fmfTrigger.style.opacity = 0;
        }
      });
    }
  }
}

export default ProductFormHandler;
